import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Table, Input, Tabs, Form, InputNumber, Dropdown, Menu,
  Button, message, Spin, Popover, List,
} from "antd";
import moment from "moment";
import { Link } from "react-router-dom";
import {
  CheckCircle, DollarSign, AlertCircle, Eye, Printer, Edit,
  Filter, Download, Maximize, Minimize, RefreshCw, Calendar,
  FileText, CreditCard, X, RotateCcw,
} from "react-feather";
import Select from "react-select";
import axios from "axios";
import Swal from "sweetalert2";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import exportService   from "../../pages/hms/utils/exportService";
import { renderDate }  from "./utils/dateFormatter";
import { DownOutlined } from "@ant-design/icons";
import DatePicker from "react-bootstrap-daterangepicker";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap-daterangepicker/daterangepicker.css";
import PrintReportPreview from "./reports/PrintReportPreview";
import PosModuleTopNav  from "./components/navigation/PosModuleTopNav";

// ─── Token getter ─────────────────────────────────────────────────────────────
const getToken = () => localStorage.getItem("token");

// ─── Bill Preview Drawer ──────────────────────────────────────────────────────
/**
 * BillPreviewDrawer — right-side slide-in panel showing the bill in thermal
 * receipt style (320px wide). Shows real data from the API. Two actions:
 *   • Re-print  → PATCH /api/pos/orders/:order_id/reprint (sends to agent)
 *   • Close     → dismisses the drawer
 */
const BillPreviewDrawer = ({ order, settings, onClose, onReprint, reprinting }) => {
  if (!order) return null;

  const currencyCode = localStorage.getItem("currencycode") || "₹";
  const bs = settings?.billing_settings || {};

  const stripHtml = (html) =>
    (html || "").replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();

  const headerLines = stripHtml(bs.print_header_text)
    .split(/\s{2,}/)
    .map((l) => l.trim())
    .filter(Boolean);

  const footerText = stripHtml(bs.print_footer_text) || "Thank You, Visit Again!";

  const grandTotal  = parseFloat(order.grand_total)        || 0;
  const subtotal    = parseFloat(order.subtotal)            || 0;
  const taxTotal    = parseFloat(order.tax)                 || 0;
  const discount    = parseFloat(order.discount_percentage) || 0;
  const delivery    = parseFloat(order.delivery_charge)     || 0;
  const partials    = Array.isArray(order.partial_payments) ? order.partial_payments : [];
  const allItems    = (order.kots || []).flatMap((k) => k.items || []);
  const billCols    = bs.bill_columns_to_show || ["item_name", "qty", "price", "total"];

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: "fixed", inset: 0,
          background: "rgba(15,23,42,0.38)",
          zIndex: 2000,
          animation: "nsBillBdIn 0.22s ease both",
        }}
      />

      {/* Drawer */}
      <div style={{
        position: "fixed", top: 0, right: 0, bottom: 0,
        width: "min(520px, 100vw)",
        background: "#f8fafc",
        boxShadow: "-28px 0 70px rgba(15,23,42,0.22)",
        zIndex: 2001,
        display: "flex", flexDirection: "column",
        animation: "nsBillDrawerIn 0.28s cubic-bezier(0.22,1,0.36,1) both",
      }}>

        {/* ── Header ── */}
        <div style={{
          background: "linear-gradient(135deg,#1d4ed8,#2563eb)",
          padding: "14px 18px",
          display: "flex", alignItems: "center", justifyContent: "space-between",
          color: "#fff", flexShrink: 0,
        }}>
          <div>
            <div style={{ fontSize: "0.68rem", textTransform: "uppercase", letterSpacing: "0.12em", opacity: 0.8 }}>
              Bill Preview
            </div>
            <div style={{ fontWeight: 950, fontSize: "1.05rem" }}>
              {order.display_order_no || order.order_number}
            </div>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button
              onClick={onReprint}
              disabled={reprinting}
              style={{
                display: "inline-flex", alignItems: "center", gap: 6,
                height: 38, borderRadius: 13,
                background: reprinting ? "rgba(255,255,255,0.2)" : "#ffffff",
                color: reprinting ? "#fff" : "#1d4ed8",
                border: 0, padding: "0 14px",
                fontWeight: 950, fontSize: "0.82rem", cursor: reprinting ? "not-allowed" : "pointer",
                transition: "all 0.16s ease",
              }}
            >
              {reprinting
                ? <><span className="ns-bill-spinner-sm" /> Sending...</>
                : <><Printer size={15} /> Re-print</>}
            </button>
            <button
              onClick={onClose}
              style={{
                width: 38, height: 38, borderRadius: 13,
                background: "rgba(255,255,255,0.15)", border: 0,
                color: "#fff", display: "inline-flex",
                alignItems: "center", justifyContent: "center", cursor: "pointer",
              }}
            >
              <X size={18} />
            </button>
          </div>
        </div>

        {/* ── Scrollable body ── */}
        <div style={{
          flex: 1, overflowY: "auto", padding: "20px 16px",
          display: "flex", justifyContent: "center",
          scrollbarWidth: "thin", scrollbarColor: "#93c5fd #e2e8f0",
        }}>

          {/* ── Thermal receipt ── */}
          <div style={{
            width: 320, fontFamily: "'Courier New', monospace",
            fontSize: 12, background: "#fff",
            border: "1px dashed #d1d5db",
            borderRadius: 8, padding: "16px 14px",
            boxShadow: "0 10px 30px rgba(15,23,42,0.10)",
          }}>

            {/* Restaurant header */}
            <div style={{ textAlign: "center", marginBottom: 10 }}>
              {headerLines.length > 0 ? (
                <>
                  <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 3 }}>
                    {headerLines[0]}
                  </div>
                  {headerLines.slice(1).map((l, i) => (
                    <div key={i} style={{ fontSize: 11, color: "#555" }}>{l}</div>
                  ))}
                </>
              ) : (
                <div style={{ fontWeight: 700, fontSize: 14 }}>TAX INVOICE</div>
              )}
            </div>

            <div style={{ borderTop: "1px dashed #ccc", margin: "8px 0" }} />

            {/* Bill meta */}
            <div style={{ fontSize: 11, marginBottom: 6 }}>
              {[
                ["Bill No", order.display_order_no || order.order_number],
                bs.show_business_date && ["Date", moment(order.business_date).format("DD-MMM-YYYY")],
                order.room_number && ["Room", order.room_number],
                order.order_type && ["Type", order.order_type],
                order.customer_phone && ["Phone", order.customer_phone],
              ].filter(Boolean).map(([label, val]) => (
                <div key={label} style={{ display: "flex", justifyContent: "space-between" }}>
                  <span style={{ color: "#555" }}>{label}</span>
                  <span style={{ fontWeight: 700 }}>{val}</span>
                </div>
              ))}
            </div>

            {/* Complementary badge */}
            {order.is_complementary && (
              <div style={{
                textAlign: "center", fontWeight: 700, fontSize: 11,
                background: "#fef9c3", color: "#713f12",
                border: "1px solid #fde68a",
                borderRadius: 6, padding: "3px 0", margin: "6px 0",
              }}>
                ★ COMPLIMENTARY — NO CHARGE ★
              </div>
            )}

            <div style={{ borderTop: "1px dashed #ccc", margin: "8px 0" }} />

            {/* Items table */}
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
              <thead>
                <tr style={{ borderBottom: "1px dashed #ccc" }}>
                  {billCols.includes("item_name") && <th style={{ textAlign: "left",  padding: "3px 2px", fontWeight: 700 }}>Item</th>}
                  {billCols.includes("qty")       && <th style={{ textAlign: "center", padding: "3px 2px", fontWeight: 700 }}>Qty</th>}
                  {billCols.includes("price")     && <th style={{ textAlign: "right",  padding: "3px 2px", fontWeight: 700 }}>Rate</th>}
                  {billCols.includes("total")     && <th style={{ textAlign: "right",  padding: "3px 2px", fontWeight: 700 }}>Amt</th>}
                </tr>
              </thead>
              <tbody>
                {allItems.length > 0 ? allItems.map((item, i) => (
                  <tr key={i} style={{ borderBottom: "1px dotted #eee" }}>
                    {billCols.includes("item_name") && (
                      <td style={{ padding: "3px 2px" }}>{item.name}</td>
                    )}
                    {billCols.includes("qty") && (
                      <td style={{ textAlign: "center", padding: "3px 2px" }}>{parseFloat(item.qty)}</td>
                    )}
                    {billCols.includes("price") && (
                      <td style={{ textAlign: "right", padding: "3px 2px" }}>
                        {parseFloat(item.price_standard || 0).toFixed(2)}
                      </td>
                    )}
                    {billCols.includes("total") && (
                      <td style={{ textAlign: "right", padding: "3px 2px", fontWeight: 700 }}>
                        {parseFloat(item.amount || 0).toFixed(2)}
                      </td>
                    )}
                  </tr>
                )) : (
                  <tr>
                    <td colSpan={4} style={{ textAlign: "center", color: "#999", padding: "8px 0", fontSize: 10 }}>
                      Item details not available in list view.<br />Open order to see full breakdown.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>

            <div style={{ borderTop: "1px dashed #ccc", margin: "8px 0" }} />

            {/* Totals */}
            {[
              ["Subtotal",         subtotal.toFixed(2)],
              taxTotal > 0 && ["Tax",            taxTotal.toFixed(2)],
              discount > 0 && [`Discount (${discount}%)`, `-${(grandTotal * discount / 100).toFixed(2)}`],
              delivery > 0 && ["Delivery",       delivery.toFixed(2)],
            ].filter(Boolean).map(([label, val]) => (
              <div key={label} style={{ display: "flex", justifyContent: "space-between", fontSize: 11, margin: "2px 0" }}>
                <span style={{ color: "#555" }}>{label}</span>
                <span>{currencyCode}{val}</span>
              </div>
            ))}

            <div style={{
              display: "flex", justifyContent: "space-between",
              fontWeight: 700, fontSize: 13,
              borderTop: "1px dashed #ccc", marginTop: 6, paddingTop: 6,
            }}>
              <span>TOTAL</span>
              <span>{currencyCode}{grandTotal.toFixed(2)}</span>
            </div>

            <div style={{ borderTop: "1px dashed #ccc", margin: "8px 0" }} />

            {/* Payment */}
            {partials.length > 0 ? (
              <div style={{ fontSize: 11 }}>
                <div style={{ fontWeight: 700, marginBottom: 3 }}>Payment:</div>
                {partials.map((p, i) => (
                  <div key={i} style={{ display: "flex", justifyContent: "space-between" }}>
                    <span>{p.method}</span>
                    <span>{currencyCode}{parseFloat(p.amount || 0).toFixed(2)}</span>
                  </div>
                ))}
              </div>
            ) : order.payment_method ? (
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11 }}>
                <span>Paid via</span>
                <span style={{ fontWeight: 700 }}>{order.payment_method}</span>
              </div>
            ) : null}

            <div style={{ borderTop: "1px dashed #ccc", margin: "8px 0" }} />

            {/* Footer */}
            <div style={{ textAlign: "center", fontSize: 11, color: "#555" }}>
              {footerText.split(/\s{2,}/).map((l, i) => (
                <div key={i}>{l}</div>
              ))}
            </div>

            {/* Reprint indicator */}
            <div style={{
              textAlign: "center", marginTop: 10, fontSize: 10,
              color: "#94a3b8", borderTop: "1px dotted #e2e8f0", paddingTop: 6,
            }}>
              Preview only — actual print goes to kitchen printer
            </div>
          </div>
        </div>

        {/* ── Footer actions ── */}
        <div style={{
          flexShrink: 0, background: "#fff",
          borderTop: "1px solid rgba(15,23,42,0.08)",
          padding: "12px 16px", display: "flex", gap: 10,
        }}>
          <button
            onClick={onReprint}
            disabled={reprinting}
            style={{
              flex: 1, height: 46, borderRadius: 14, border: 0,
              background: reprinting
                ? "#94a3b8"
                : "linear-gradient(135deg,#1d4ed8,#2563eb)",
              color: "#fff", fontWeight: 950, fontSize: "0.9rem",
              display: "inline-flex", alignItems: "center",
              justifyContent: "center", gap: 8,
              cursor: reprinting ? "not-allowed" : "pointer",
              boxShadow: "0 12px 28px rgba(37,99,235,0.22)",
              transition: "all 0.16s ease",
            }}
          >
            {reprinting
              ? <><span className="ns-bill-spinner-sm" /> Sending to printer...</>
              : <><Printer size={17} /> Print from Preview</>}
          </button>
          <button
            onClick={onClose}
            style={{
              height: 46, borderRadius: 14,
              border: "1px solid rgba(15,23,42,0.10)",
              background: "#f8fafc", color: "#475569",
              fontWeight: 950, padding: "0 18px", cursor: "pointer",
            }}
          >
            Close
          </button>
        </div>
      </div>

      <style>{`
        @keyframes nsBillBdIn    { from { opacity:0 } to { opacity:1 } }
        @keyframes nsBillDrawerIn { from { transform:translateX(100%) } to { transform:translateX(0) } }
        .ns-bill-spinner-sm {
          width: 14px; height: 14px; border-radius: 999px;
          border: 2px solid rgba(255,255,255,0.3);
          border-top-color: #fff;
          animation: ns-spin 0.7s linear infinite;
          flex: 0 0 auto;
        }
        @keyframes ns-spin { to { transform: rotate(360deg); } }
      `}</style>
    </>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────

const PosBillsList = () => {
  const businessDate = localStorage.getItem("posbusiness_date");
  const timestamp    = new Date().toISOString().replace(/[:.-]/g, "");
  const suffix       = `_business_date_${businessDate}_${timestamp}`;
  const currencyCode = localStorage.getItem("currencycode") || "₹";

  const [isFullScreen,           setFullScreen]           = useState(false);
  const [searchText,             setSearchText]           = useState("");
  const [orders,                 setOrders]               = useState([]);
  const [applicableTaxes,        setApplicableTaxes]      = useState([]);
  const [listLoading,            setListLoading]          = useState(false);
  const [previewLoading,         setPreviewLoading]       = useState(false);
  const [reprinting,             setReprinting]           = useState(false);
  const [error,                  setError]                = useState(null);
  const [modalVisible,           setModalVisible]         = useState(false);
  const [selectedOrder,          setSelectedOrder]        = useState(null);
  const [form]                                            = Form.useForm();
  const [pdfVisible,             setPdfVisible]           = useState(false);
  const [pdfData,                setPdfData]              = useState(null);

  // Bill preview drawer
  const [previewOrder,           setPreviewOrder]         = useState(null);  // full fetched order
  const [previewSettings,        setPreviewSettings]      = useState(null);  // billing_settings
  const [showPreview,            setShowPreview]          = useState(false);

  const [activeTab,              setActiveTab]            = useState("1");
  const { Search }  = Input;
  const { TabPane } = Tabs;
  const apiURL = process.env.REACT_APP_API_URL || "https://api.example.com";

  const [dateRange, setDateRange] = useState({
    startDate: moment(businessDate).startOf("day"),
    endDate:   moment(businessDate).startOf("day"),
  });
  const [filters, setFilters] = useState({
    order_type: [], status: [], payment_method: [],
    is_settled: null, is_complementary: null,
  });
  const maximizeBtnRef = useRef(null);

  // ── Fullscreen ────────────────────────────────────────────────────────────
  useEffect(() => {
    const handleClick = () => {
      if (!document.fullscreenElement) {
        document.documentElement.requestFullscreen().catch(console.error);
        setFullScreen(true);
      } else {
        document.exitFullscreen().catch(console.error);
        setFullScreen(false);
      }
    };
    const handleFSChange = () => setFullScreen(!!document.fullscreenElement);
    const btn = maximizeBtnRef.current;
    if (btn) btn.addEventListener("click", handleClick);
    document.addEventListener("fullscreenchange", handleFSChange);
    return () => {
      if (btn) btn.removeEventListener("click", handleClick);
      document.removeEventListener("fullscreenchange", handleFSChange);
      if (document.fullscreenElement) document.exitFullscreen().catch(console.error);
    };
  }, []);

  useEffect(() => {
    fetchOrders(dateRange.startDate, dateRange.endDate);
  }, []);

  // ── Fetch orders list ─────────────────────────────────────────────────────
  const fetchOrders = async (startDate, endDate) => {
    if (!startDate || !endDate || !moment(startDate).isValid() || !moment(endDate).isValid()) {
      setError("Invalid date range provided.");
      setOrders([]); setApplicableTaxes([]);
      return;
    }
    setListLoading(true); setError(null);
    const token = getToken();
    if (!token) {
      setListLoading(false);
      setError("Authentication token missing. Please log in again.");
      window.location.href = "/login"; return;
    }
    try {
      const response = await axios.get(`${apiURL}/pos/orders/list-bills`, {
        params: { startDate: startDate.format("YYYY-MM-DD"), endDate: endDate.format("YYYY-MM-DD") },
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.data || !Array.isArray(response.data.orders)) {
        throw new Error("Invalid response format: orders must be an array.");
      }
      const parsedOrders = response.data.orders.map((o) => ({
        ...o,
        subtotal:    Number(o.subtotal)    || 0,
        tax:         Number(o.tax)         || 0,
        grand_total: Number(o.grand_total) || 0,
      }));
      setOrders(parsedOrders);
      setApplicableTaxes(response.data.applicableTaxes || []);
      return { data: { orders: parsedOrders, applicableTaxes: response.data.applicableTaxes || [] } };
    } catch (err) {
      if (err.response?.status === 401) {
        setError("Session expired. Please log in again.");
        localStorage.removeItem("token");
        window.location.href = "/login";
      } else {
        const msg = err.response?.data?.message || err.message || "Failed to load orders.";
        setError(msg);
        toast.error(msg, { autoClose: 3000 });
      }
      setOrders([]); setApplicableTaxes([]);
    } finally {
      setListLoading(false);
    }
  };

  // ── Date navigation ───────────────────────────────────────────────────────
  const handleDateChange = async (param) => {
    setDateRange((prevRange) => {
      let newRange;
      if (typeof param === "string") {
        const delta = param === "prev" ? -1 : 1;
        newRange = {
          startDate: prevRange.startDate.clone().add(delta, "day").startOf("day"),
          endDate:   prevRange.endDate.clone().add(delta, "day").startOf("day"),
        };
      } else {
        newRange = {
          startDate: moment(param.start).startOf("day"),
          endDate:   moment(param.end).startOf("day"),
        };
      }
      if (!newRange.startDate.isValid() || !newRange.endDate.isValid() || newRange.startDate.isAfter(newRange.endDate)) {
        setError("Invalid date range."); return prevRange;
      }
      fetchOrders(newRange.startDate, newRange.endDate)
        .then((r) => { if (r) { setOrders(r.data.orders); setApplicableTaxes(r.data.applicableTaxes); } })
        .catch(() => {});
      return newRange;
    });
  };

  // ── Open bill preview (fetch real order + settings) ───────────────────────
  const handleOpenPreview = useCallback(async (listRow) => {
    setPreviewLoading(true);
    setShowPreview(true);
    setPreviewOrder({ ...listRow, kots: [] }); // show immediately with list data
    try {
      const token = getToken();
      // Fetch full order details (has kots with items)
      const [orderRes, settingsRes] = await Promise.all([
        axios.get(`${apiURL}/pos/orders/${listRow.id}`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${apiURL}/setting-pos`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);
      setPreviewOrder(orderRes.data.order || orderRes.data);
      setPreviewSettings(settingsRes.data.settings || {});
    } catch (err) {
      toast.error("Failed to load bill details: " + (err.response?.data?.error || err.message));
    } finally {
      setPreviewLoading(false);
    }
  }, [apiURL]);

  // ── Reprint — sends to kitchen agent via backend ──────────────────────────
  const handleReprint = useCallback(async (orderId) => {
    if (!orderId) { toast.error("No order selected for reprint."); return; }
    setReprinting(true);
    try {
      const token = getToken();
      await axios.patch(`${apiURL}/pos/orders/${orderId}/reprint`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Reprint sent to kitchen printer!");
    } catch (err) {
      toast.error("Reprint failed: " + (err.response?.data?.error || err.message));
    } finally {
      setReprinting(false);
    }
  }, [apiURL]);

  const handleClosePrev = () => {
    setShowPreview(false);
    setPreviewOrder(null);
    setPreviewSettings(null);
  };

  // ── Legacy: direct print icon in table row ────────────────────────────────
  // Old handlePrintBill opened window.open with static items.
  // Now it calls reprint backend — consistent with agent-based printing.
  const handlePrintBill = useCallback((row) => {
    handleReprint(row.id);
  }, [handleReprint]);

  const handleEditOrder = (order) => {
    toast.info(`Edit for order ${order.order_number} not yet implemented.`, { autoClose: 3000 });
  };

  // ── Filtering ─────────────────────────────────────────────────────────────
  const settledOrders   = orders.filter((o) => o.is_settled);
  const unsettledOrders = orders.filter((o) => !o.is_settled);

  const applyFilters = (data) =>
    data
      .filter((o) => {
        if (filters.order_type.length     && !filters.order_type.includes(o.order_type))         return false;
        if (filters.status.length         && !filters.status.includes(o.status))                 return false;
        if (filters.payment_method.length && !filters.payment_method.includes(o.payment_method)) return false;
        if (filters.is_settled !== null   && o.is_settled !== (filters.is_settled === "paid"))   return false;
        if (filters.is_complementary !== null && o.is_complementary !== (filters.is_complementary === "yes")) return false;
        return true;
      })
      .filter((o) =>
        !searchText ||
        (o.order_number  || "").toLowerCase().includes(searchText.toLowerCase()) ||
        (o.display_order_no || "").toLowerCase().includes(searchText.toLowerCase()) ||
        (o.customer_phone || "").toLowerCase().includes(searchText.toLowerCase()) ||
        (o.room_number   || "").toLowerCase().includes(searchText.toLowerCase()) ||
        (o.payment_method || "").toLowerCase().includes(searchText.toLowerCase())
      );

  const filteredSettledOrders   = applyFilters(settledOrders);
  const filteredUnsettledOrders = applyFilters(unsettledOrders);

  // ── Columns ───────────────────────────────────────────────────────────────
  const taxColumns = applicableTaxes.map((taxName) => ({
    title: `${taxName} ${currencyCode}`,
    dataIndex: taxName,
    render: (amount) => (amount || 0).toFixed(2),
    sorter: (a, b) => (a[taxName] || 0) - (b[taxName] || 0),
  }));

  const baseColumns = [
    {
      title: "Bill",
      dataIndex: "order_number",
      fixed: "left",
      width: 200,
      sorter: (a, b) => a.order_number.localeCompare(b.order_number),
      render: (text, record) => (
        <div>
          {/* Bill number as clickable link */}
          <a
            href="#"
            onClick={(e) => { e.preventDefault(); handleOpenPreview(record); }}
            style={{ fontWeight: 950, color: "#1d4ed8", fontSize: "0.9rem" }}
          >
            {record.display_order_no || text}
          </a>
          <div style={{ fontSize: "0.74rem", color: "#64748b", marginTop: 2 }}>
            {record.order_type}
            {record.room_number ? ` · Room ${record.room_number}` : ""}
            {record.is_complementary ? (
              <span style={{
                marginLeft: 5, background: "#fef9c3", color: "#713f12",
                borderRadius: 999, padding: "1px 7px", fontSize: "0.68rem", fontWeight: 950,
              }}>COMP</span>
            ) : null}
          </div>

          {/* Action pill buttons */}
          <div style={{ display: "flex", gap: 5, marginTop: 6 }}>
            <button
              onClick={() => handleOpenPreview(record)}
              title="Preview Bill"
              style={{
                display: "inline-flex", alignItems: "center", gap: 4,
                height: 26, borderRadius: 8, border: "1px solid #bfdbfe",
                background: "#eff6ff", color: "#1d4ed8",
                fontSize: "0.68rem", fontWeight: 950, padding: "0 8px", cursor: "pointer",
              }}
            >
              <Eye size={12} /> Preview
            </button>
            <button
              onClick={() => handlePrintBill(record)}
              title="Send to kitchen printer"
              style={{
                display: "inline-flex", alignItems: "center", gap: 4,
                height: 26, borderRadius: 8, border: "1px solid #bbf7d0",
                background: "#f0fdf4", color: "#166534",
                fontSize: "0.68rem", fontWeight: 950, padding: "0 8px", cursor: "pointer",
              }}
            >
              <Printer size={12} /> Print
            </button>
          </div>
        </div>
      ),
    },
    {
      title: "Customer",
      key: "customer",
      render: (_, r) => (
        <div style={{ fontSize: "0.82rem" }}>
          {r.customer_phone
            ? <div style={{ fontWeight: 850, color: "#0f172a" }}>{r.customer_phone}</div>
            : <div style={{ color: "#94a3b8", fontSize: "0.74rem" }}>—</div>}
          <div style={{ color: "#64748b", fontSize: "0.74rem" }}>{r.source}</div>
        </div>
      ),
    },
    {
      title: "Status",
      key: "status",
      width: 110,
      render: (_, r) => (
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <span style={{
            display: "inline-block", borderRadius: 999,
            padding: "2px 10px", fontSize: "0.7rem", fontWeight: 950,
            background: r.is_settled ? "#ecfdf5" : "#fef3c7",
            color:      r.is_settled ? "#166534" : "#92400e",
            border: `1px solid ${r.is_settled ? "#bbf7d0" : "#fde68a"}`,
          }}>
            {r.is_settled ? "Settled" : "Unsettled"}
          </span>
          {r.is_complementary && (
            <span style={{
              display: "inline-block", borderRadius: 999,
              padding: "2px 8px", fontSize: "0.68rem", fontWeight: 950,
              background: "#fef9c3", color: "#713f12",
              border: "1px solid #fde68a",
            }}>Gift</span>
          )}
        </div>
      ),
    },
    {
      title: `Subtotal ${currencyCode}`,
      dataIndex: "subtotal",
      sorter: (a, b) => (a.grand_total - a.tax) - (b.grand_total - b.tax),
      render: (_, r) => ((r.grand_total || 0) - (r.tax || 0)).toFixed(2),
    },
    // dynamic tax columns inserted below
    {
      title: `Tax ${currencyCode}`,
      dataIndex: "tax",
      render: (a) => (a || 0).toFixed(2),
    },
    {
      title: `Discount %`,
      dataIndex: "discount_percentage",
      sorter: (a, b) => a.discount_percentage - b.discount_percentage,
      render: (p) => `${(p || 0).toFixed(2)}%`,
    },
    {
      title: `Delivery ${currencyCode}`,
      dataIndex: "delivery_charge",
      sorter: (a, b) => a.delivery_charge - b.delivery_charge,
      render: (a) => (a || 0).toFixed(2),
    },
    {
      title: `Grand Total ${currencyCode}`,
      dataIndex: "grand_total",
      sorter: (a, b) => a.grand_total - b.grand_total,
      render: (a) => <strong style={{ color: "#16a34a" }}>{(a || 0).toFixed(2)}</strong>,
    },
    {
      title: "Payment",
      dataIndex: "payment_method",
      render: (pm, r) => {
        const pp = r.partial_payments || [];
        return pp.length > 0 ? (
          <Popover
            content={
              <List
                size="small"
                dataSource={pp}
                renderItem={(item) => (
                  <List.Item>
                    <strong>{item.method}</strong>: {currencyCode}{item.amount}
                  </List.Item>
                )}
              />
            }
            title="Partial Payments"
            trigger="click"
          >
            <Button type="link" size="small">
              {pm} <span style={{ fontSize: "0.72rem", color: "#64748b" }}>({pp.length})</span>
            </Button>
          </Popover>
        ) : (
          <span style={{ fontSize: "0.82rem" }}>{pm || "—"}</span>
        );
      },
    },
    {
      title: "Date",
      dataIndex: "business_date",
      render: (d) => <span className="date-box check-in-box">{renderDate(d)}</span>,
      sorter: (a, b) => moment(a.business_date).unix() - moment(b.business_date).unix(),
    },
    {
      title: "Action",
      key: "action",
      render: (_, r) =>
        !r.is_settled ? (
          <button
            className="btn btn-sm"
            onClick={() => handleSettleOrder(r)}
            style={{
              background: "#1d4ed8", color: "#fff", borderRadius: 10,
              fontWeight: 950, fontSize: "0.76rem", padding: "4px 12px", border: 0,
            }}
          >
            Settle
          </button>
        ) : null,
    },
  ];

  // Insert tax columns before Tax column
  const taxColIdx = baseColumns.findIndex((c) => c.dataIndex === "tax");
  const columns = [
    ...baseColumns.slice(0, taxColIdx),
    ...taxColumns,
    ...baseColumns.slice(taxColIdx),
  ];

  // ── Export helpers (preserved verbatim) ───────────────────────────────────
  const exportHeaders = [
    { key: "order_number",      title: "Order Number" },
    { key: "display_order_no",  title: "Display Order No" },
    { key: "room_number",       title: "Room Number" },
    { key: "customer_phone",    title: "Customer Phone" },
    { key: "order_type",        title: "Order Type" },
    { key: "source",            title: "Source" },
    { key: "status",            title: "Status" },
    { key: "is_settled",        title: "Settled" },
    { key: "billing_type",      title: "Billing Type" },
    { key: "subtotal",          title: `Subtotal ${currencyCode}` },
    ...applicableTaxes.map((t) => ({ key: t, title: `${t} ${currencyCode}` })),
    { key: "tax",               title: `Tax ${currencyCode}` },
    { key: "discount_percentage", title: "Discount %" },
    { key: "delivery_charge",   title: `Delivery Charge ${currencyCode}` },
    { key: "grand_total",       title: `Grand Total ${currencyCode}` },
    { key: "payment_method",    title: "Payment Method" },
    { key: "is_complementary",  title: "Complementary" },
    { key: "business_date",     title: "Business Date" },
  ];

  const exportData = (data) =>
    data.map((o) => ({
      order_number:       o.order_number,
      display_order_no:   o.display_order_no,
      room_number:        o.room_number        || "",
      customer_phone:     o.customer_phone     || "",
      order_type:         o.order_type,
      source:             o.source,
      status:             o.status,
      is_settled:         o.is_settled ? "Yes" : "No",
      billing_type:       o.billing_type,
      subtotal:           ((o.grand_total || 0) - (o.tax || 0)).toFixed(2),
      ...applicableTaxes.reduce((acc, t) => ({ ...acc, [t]: (o[t] || 0).toFixed(2) }), {}),
      tax:                (o.tax               || 0).toFixed(2),
      discount_percentage:(o.discount_percentage || 0).toFixed(2),
      delivery_charge:    (o.delivery_charge   || 0).toFixed(2),
      grand_total:        (o.grand_total        || 0).toFixed(2),
      payment_method:     o.payment_method     || "",
      is_complementary:   o.is_complementary ? "Yes" : "No",
      business_date:      renderDate(o.business_date),
    }));

  const getTotals = (data) => {
    const totalOrders    = data.length;
    const totalSubtotal  = data.reduce((s, o) => s + ((o.grand_total || 0) - (o.tax || 0)), 0).toFixed(2);
    const totalTax       = data.reduce((s, o) => s + (o.tax || 0), 0).toFixed(2);
    const totalGrandTotal= data.reduce((s, o) => s + (o.grand_total || 0), 0).toFixed(2);
    const taxTotals      = applicableTaxes.reduce((acc, t) => ({
      ...acc, [t]: data.reduce((s, o) => s + (o[t] || 0), 0).toFixed(2),
    }), {});
    const expectedSub    = (parseFloat(totalGrandTotal) - parseFloat(totalTax)).toFixed(2);
    const subValid       = Math.abs(parseFloat(totalSubtotal) - parseFloat(expectedSub)) < 0.01;

    return [
      { key: "Total Orders",      value: totalOrders },
      { key: "Total Subtotal",    value: `${currencyCode}${totalSubtotal}` },
      ...applicableTaxes.map((t) => ({ key: `${t} Total`, value: `${currencyCode}${taxTotals[t]}` })),
      { key: "Total Tax",         value: `${currencyCode}${totalTax}` },
      { key: "Total Grand Total", value: `${currencyCode}${totalGrandTotal}` },
      {
        key: "Subtotal Verification",
        value: subValid
          ? `✅ Subtotal (${currencyCode}${totalSubtotal}) = Grand Total - Tax`
          : `❌ Mismatch: Subtotal (${currencyCode}${totalSubtotal}) ≠ Grand Total - Tax`,
      },
    ];
  };

  const handleExportXLS = () => {
    const d = activeTab === "1" ? filteredSettledOrders : filteredUnsettledOrders;
    exportService.exportToXLS(exportData(d), exportHeaders, `pos_bills_list${suffix}.xlsx`, "Sales Register", getTotals(d));
  };
  const handleExportCSV = () => {
    const d = activeTab === "1" ? filteredSettledOrders : filteredUnsettledOrders;
    exportService.exportToCSV(exportData(d), exportHeaders, `pos_bills_list${suffix}.csv`, getTotals(d));
  };
  const handleExportPDF = async () => {
    const d = activeTab === "1" ? filteredSettledOrders : filteredUnsettledOrders;
    const t = getTotals(d);
    exportService.exportToPDF(exportData(d), exportHeaders, "Sales Register", `pos_bills_list${suffix}.pdf`, false, true, t, t);
  };
  const handlePrint = async () => {
    try {
      setPreviewLoading(true);
      const d = activeTab === "1" ? filteredSettledOrders : filteredUnsettledOrders;
      const t = getTotals(d);
      const pdfBlob = await exportService.exportToPDF(exportData(d), exportHeaders, "Sales Register", null, true, true, t, t);
      const pdfUrl = URL.createObjectURL(pdfBlob);
      setPdfData(pdfUrl); setPdfVisible(true);
    } catch { message.error("Failed to load PDF preview."); }
    finally { setPreviewLoading(false); }
  };

  const downloadMenu = (
    <Menu>
      <Menu.Item key="xls"   onClick={handleExportXLS}>Download XLS</Menu.Item>
      <Menu.Item key="csv"   onClick={handleExportCSV}>Download CSV</Menu.Item>
      <Menu.Item key="pdf"   onClick={handleExportPDF}>Download PDF</Menu.Item>
      <Menu.Item key="print" onClick={handlePrint}>Print Preview</Menu.Item>
    </Menu>
  );

  // ── Settle order modal ────────────────────────────────────────────────────
  const handleSettleOrder = (order) => {
    setSelectedOrder(order);
    setModalVisible(true);
    form.setFieldsValue({ payments: [{ method: "", amount: "" }] });
  };

  const handleModalOk = async () => {
    try {
      await form.validateFields();
      setModalVisible(false);
      toast.success("Order settled successfully!");
    } catch { toast.error("Error settling order."); }
  };

  // ── Filter panel ──────────────────────────────────────────────────────────
  const filterContent = (
    <div style={{ width: 300 }}>
      <Form layout="vertical">
        {[
          { label: "Order Type", key: "order_type", isMulti: true, opts: [
            { value: "Room Service", label: "Room Service" },
            { value: "Counter",      label: "Counter"      },
            { value: "Restaurant",   label: "Restaurant"   },
          ]},
          { label: "Status", key: "status", isMulti: true, opts: [
            { value: "Pending",   label: "Pending"   },
            { value: "Completed", label: "Completed" },
          ]},
          { label: "Payment Method", key: "payment_method", isMulti: true, opts: [
            { value: "Cash",        label: "Cash"        },
            { value: "Card",        label: "Card"        },
            { value: "Room Credit", label: "Room Credit" },
            { value: "UPI",         label: "UPI"         },
          ]},
        ].map(({ label, key, isMulti, opts }) => (
          <Form.Item key={key} label={label}>
            <Select
              isMulti={isMulti}
              options={opts}
              value={filters[key].map((v) => ({ value: v, label: v }))}
              onChange={(sel) => setFilters({ ...filters, [key]: (sel || []).map((o) => o.value) })}
              styles={{ control: (b) => ({ ...b, borderRadius: 8 }) }}
            />
          </Form.Item>
        ))}
        <Form.Item label="Billing Status">
          <Select
            options={[{ value: "paid", label: "Paid" }, { value: "unpaid", label: "Unpaid" }]}
            value={filters.is_settled ? { value: filters.is_settled, label: filters.is_settled === "paid" ? "Paid" : "Unpaid" } : null}
            onChange={(s) => setFilters({ ...filters, is_settled: s?.value || null })}
            isClearable
            styles={{ control: (b) => ({ ...b, borderRadius: 8 }) }}
          />
        </Form.Item>
        <Form.Item label="Complementary">
          <Select
            options={[{ value: "yes", label: "Yes" }, { value: "no", label: "No" }]}
            value={filters.is_complementary ? { value: filters.is_complementary, label: filters.is_complementary === "yes" ? "Yes" : "No" } : null}
            onChange={(s) => setFilters({ ...filters, is_complementary: s?.value || null })}
            isClearable
            styles={{ control: (b) => ({ ...b, borderRadius: 8 }) }}
          />
        </Form.Item>
        <Button type="default" onClick={() => setFilters({ order_type: [], status: [], payment_method: [], is_settled: null, is_complementary: null })}>
          Clear Filters
        </Button>
      </Form>
    </div>
  );

  // ── Stats ─────────────────────────────────────────────────────────────────
  const activeTabData = activeTab === "1" ? filteredSettledOrders : filteredUnsettledOrders;
  const activeFilterCount = filters.order_type.length + filters.status.length + filters.payment_method.length + (filters.is_settled ? 1 : 0) + (filters.is_complementary ? 1 : 0);

  const formatMoney = (a) => `${currencyCode}${Number(a || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const pageStats = {
    totalBills:         orders.length,
    settledBills:       settledOrders.length,
    outstandingBills:   unsettledOrders.length,
    totalRevenue:       orders.reduce((s, o) => s + (Number(o.grand_total) || 0), 0),
    outstandingRevenue: unsettledOrders.reduce((s, o) => s + (Number(o.grand_total) || 0), 0),
    complementaryBills: orders.filter((o) => o.is_complementary).length,
  };

  const renderStatCard = ({ label, value, helper, icon, tone = "blue" }) => (
    <div className={`ns-posbills-stat ns-posbills-stat-${tone}`}>
      <div>
        <span>{label}</span>
        <strong>{value}</strong>
        <small>{helper}</small>
      </div>
      <div className="ns-posbills-stat-icon">{icon}</div>
    </div>
  );

  // ── Table renderer ────────────────────────────────────────────────────────
  const renderTable = (data, includeAction = false) => (
    <>
      <Table
        columns={includeAction ? columns : columns.filter((c) => c.key !== "action")}
        dataSource={data}
        pagination={{ pageSize: 30 }}
        className="table-striped ns-posbills-table"
        rowKey={(r) => r.id}
        scroll={{ x: 1400, y: 520 }}
      />
      <div className="totals-container">
        <h4>Totals ({includeAction ? "Outstanding" : "Settled"} Bills)</h4>
        {getTotals(data).map(({ key, value }) => (
          <p key={key} className={
            key === "Subtotal Verification"
              ? value.includes("Mismatch") ? "verification-fail" : "verification-pass"
              : ""
          }>
            <strong>{key}:</strong> <span>{value}</span>
          </p>
        ))}
      </div>
    </>
  );

  const emptyState = (title, desc) => (
    <div className="no-data-container text-center mt-4">
      <img
        src="https://ui-cdn.digitalocean.com/apps/ui-spaces/static/media/spaces-empty.5e1d7947c43b7e336b14e421d4a86639.svg"
        alt="No data" style={{ width: 150, marginBottom: 20 }}
      />
      <h3>{title}</h3>
      <p>{desc}</p>
    </div>
  );

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <div className="page-wrapper posmt-4">
      <style>{`
        /* All original CSS from PosBillsList preserved verbatim + additions */
        .ns-posbills-page {
          min-height: calc(100vh - 70px);
          background:
            radial-gradient(circle at 12% 3%, rgba(37,99,235,0.08), transparent 30%),
            radial-gradient(circle at 86% 0%, rgba(34,197,94,0.10), transparent 26%),
            #f8fafc;
          padding-bottom: 24px;
        }
        .ns-posbills-hero {
          background:
            radial-gradient(circle at top left, rgba(37,99,235,0.16), transparent 34%),
            radial-gradient(circle at top right, rgba(34,197,94,0.10), transparent 30%),
            linear-gradient(135deg,#ffffff 0%,#f8fbff 100%);
          border: 1px solid rgba(15,23,42,0.08);
          border-radius: 24px;
          box-shadow: 0 18px 48px rgba(15,23,42,0.08);
          padding: 16px 18px;
          margin-bottom: 16px;
        }
        .ns-posbills-hero-top {
          display: grid;
          grid-template-columns: minmax(260px,0.85fr) minmax(420px,1.35fr);
          align-items: start;
          gap: 14px;
        }
        .ns-posbills-eyebrow { color:#2563eb; font-size:0.72rem; text-transform:uppercase; letter-spacing:0.14em; font-weight:900; margin-bottom:6px; }
        .ns-posbills-title { color:#001f5b; font-size:1.55rem; font-weight:950; line-height:1.18; margin:0; display:flex; align-items:center; gap:10px; }
        .ns-posbills-subtitle { display: none !important; }
        .ns-posbills-quicknav-inline { min-width:0; justify-self:end; width:100%; display:flex; justify-content:flex-end; }
        .ns-posbills-toolbar {
          grid-column:1/-1; display:flex; align-items:center;
          justify-content:flex-end; gap:10px; flex-wrap:wrap;
          padding-top:4px; border-top:1px solid rgba(15,23,42,0.06);
        }
        .ns-date-picker-trigger {
          display:inline-flex; align-items:center; gap:9px; height:42px;
          border-radius:14px; background:#ffffff;
          border:1px solid rgba(15,23,42,0.10);
          box-shadow:0 8px 22px rgba(15,23,42,0.06); padding:0 12px; cursor:pointer;
        }
        .ns-date-picker-trigger input { border:0!important; background:transparent!important; box-shadow:none!important; width:232px; padding:0; color:#0f172a; font-weight:900; cursor:pointer; }
        .ns-posbills-action-btn, .ns-posbills-icon-action {
          border-radius:14px!important; font-weight:900!important; height:42px!important;
          display:inline-flex!important; align-items:center!important; justify-content:center!important; gap:8px!important;
          box-shadow:0 8px 22px rgba(15,23,42,0.06);
        }
        .ns-posbills-icon-action { width:42px!important; padding:0!important; }
        .ns-posbills-stats { display:grid; grid-template-columns:repeat(4,minmax(0,1fr)); gap:12px; margin-top:16px; }
        .ns-posbills-stat {
          background:#fff; border:1px solid rgba(15,23,42,0.08); border-radius:18px;
          padding:14px; box-shadow:0 10px 28px rgba(15,23,42,0.06);
          display:flex; justify-content:space-between; gap:12px; align-items:flex-start;
          overflow:hidden; position:relative;
        }
        .ns-posbills-stat::after { content:""; position:absolute; left:0; right:0; bottom:0; height:4px; background:#2563eb; }
        .ns-posbills-stat-green::after { background:#22c55e; }
        .ns-posbills-stat-orange::after { background:#f59e0b; }
        .ns-posbills-stat-purple::after { background:#7c3aed; }
        .ns-posbills-stat span { display:block; color:#64748b; font-size:0.72rem; text-transform:uppercase; letter-spacing:0.05em; font-weight:900; margin-bottom:5px; }
        .ns-posbills-stat strong { color:#0f172a; font-size:1.22rem; font-weight:950; line-height:1.15; }
        .ns-posbills-stat small { color:#94a3b8; font-size:0.75rem; display:block; margin-top:5px; }
        .ns-posbills-stat-icon { width:40px; height:40px; border-radius:15px; background:#eff6ff; color:#1d4ed8; display:inline-flex; align-items:center; justify-content:center; flex:0 0 auto; }
        .ns-posbills-shell { background:#fff; border:1px solid rgba(15,23,42,0.08); border-radius:24px; box-shadow:0 18px 48px rgba(15,23,42,0.08); overflow:hidden; padding:0!important; }
        .ns-posbills-searchbar { padding:14px 16px; border-bottom:1px solid rgba(15,23,42,0.08); background:#fff; display:flex; align-items:center; justify-content:space-between; gap:12px; flex-wrap:wrap; }
        .ns-search-wrap { min-width:280px; flex:1 1 380px; max-width:560px; }
        .ns-search-wrap .ant-input-search .ant-input,
        .ns-search-wrap .ant-input-search-button { height:42px!important; }
        .ns-search-wrap .ant-input { border-radius:14px 0 0 14px!important; }
        .ns-search-wrap .ant-input-search-button { border-radius:0 14px 14px 0!important; }
        .ns-active-context { color:#64748b; font-size:0.8rem; font-weight:800; display:inline-flex; align-items:center; gap:8px; flex-wrap:wrap; }
        .ns-active-context strong { color:#0f172a; }
        .ns-posbills-tabs { padding:0 16px 18px; }
        .ns-posbills-tabs .ant-tabs-nav { margin-bottom:14px!important; }
        .ns-posbills-tabs .ant-tabs-tab-btn { font-weight:900; }
        .ns-tab-count { min-width:24px; height:22px; border-radius:999px; background:#eff6ff; color:#1d4ed8; display:inline-flex; align-items:center; justify-content:center; padding:0 8px; font-size:0.72rem; font-weight:950; margin-left:6px; }
        .ns-posbills-table .ant-table, .table-striped .ant-table { border-radius:18px!important; overflow:hidden; border:1px solid rgba(15,23,42,0.08); }
        .ns-posbills-tabs .ant-table-thead > tr > th, .table-striped .ant-table-thead > tr > th { background:#f8fafc!important; color:#0f172a!important; font-weight:950!important; font-size:0.78rem; text-transform:uppercase; letter-spacing:0.03em; }
        .ns-posbills-tabs .ant-table-tbody > tr:hover > td, .table-striped .ant-table-tbody > tr:hover > td { background:#f8fbff!important; }
        .totals-container { background:radial-gradient(circle at top left,rgba(37,99,235,0.08),transparent 34%),#fff; border:1px solid rgba(15,23,42,0.08); border-radius:20px; box-shadow:0 10px 28px rgba(15,23,42,0.06); padding:16px; margin-top:16px; display:grid; grid-template-columns:repeat(auto-fill,minmax(230px,1fr)); gap:10px; }
        .totals-container h4 { grid-column:1/-1; color:#0f172a; font-size:1rem; font-weight:950; margin:0 0 4px; }
        .totals-container p { background:#f8fafc; border:1px solid rgba(15,23,42,0.07); border-radius:15px; padding:11px 12px; margin:0; color:#64748b; }
        .totals-container p strong { display:block; color:#64748b; font-size:0.72rem; font-weight:900; margin-bottom:4px; }
        .totals-container p span { color:#0f172a; font-size:0.92rem; font-weight:950; }
        .verification-pass { background:#ecfdf5!important; color:#047857!important; border-color:#bbf7d0!important; }
        .verification-fail { background:#fef2f2!important; color:#b91c1c!important; border-color:#fecaca!important; }
        .no-data-container { min-height:300px; background:#fff; border:1px dashed rgba(100,116,139,0.30); border-radius:20px; display:flex; flex-direction:column; align-items:center; justify-content:center; padding:32px; color:#64748b; }
        .no-data-container h3 { color:#0f172a; font-weight:950; }
        .ns-modern-loader { min-height:420px; display:flex; align-items:center; justify-content:center; }
        .ns-modern-loader-card { background:#fff; border:1px solid rgba(15,23,42,0.08); border-radius:22px; box-shadow:0 18px 48px rgba(15,23,42,0.10); padding:26px 32px; display:inline-flex; gap:14px; align-items:center; color:#64748b; font-weight:900; }
        @media (max-width:1280px) { .ns-posbills-hero-top { grid-template-columns:1fr!important; } .ns-posbills-quicknav-inline { justify-self:stretch; justify-content:flex-start; } .ns-posbills-toolbar { justify-content:flex-start!important; } }
        @media (max-width:1200px) { .ns-posbills-stats { grid-template-columns:repeat(2,minmax(0,1fr)); } }
        @media (max-width:768px) { .ns-posbills-hero { padding:14px; } .ns-posbills-toolbar { justify-content:flex-start; width:100%; } .ns-date-picker-trigger input { width:190px; } .ns-posbills-stats { grid-template-columns:1fr; } .ns-posbills-searchbar { align-items:stretch; } .ns-search-wrap { min-width:100%; max-width:100%; } }
      `}</style>

      <div className="content container-fluid ns-posbills-page">

        {/* ── Hero ── */}
        <div className="ns-posbills-hero">
          <div className="ns-posbills-hero-top">
            <div>
              <div className="ns-posbills-eyebrow">POS Sales Control</div>
              <h2 className="ns-posbills-title"><FileText size={26} /> Sales Register</h2>
            </div>
            <div className="ns-posbills-quicknav-inline">
              <PosModuleTopNav activeKey="sales-register" />
            </div>
            <div className="ns-posbills-toolbar">
              <Button className="ns-posbills-icon-action" onClick={() => handleDateChange("prev")}>‹</Button>
              <DatePicker
                initialSettings={{
                  singleDatePicker: false, showDropdowns: true,
                  startDate: dateRange.startDate.toDate(),
                  endDate:   dateRange.endDate.toDate(),
                  ranges: {
                    "Today":      [moment(businessDate).startOf("day").toDate(), moment(businessDate).startOf("day").toDate()],
                    "This Week":  [moment(businessDate).startOf("week").toDate(), moment(businessDate).endOf("week").toDate()],
                    "This Month": [moment(businessDate).startOf("month").toDate(), moment(businessDate).endOf("month").toDate()],
                  },
                  locale: { format: "DD MMM YYYY", applyLabel: "Select", cancelLabel: "Cancel" },
                }}
                onCallback={(start, end) => handleDateChange({ start, end })}
              >
                <div className="ns-date-picker-trigger">
                  <Calendar size={17} />
                  <input type="text" value={`${dateRange.startDate.format("DD MMM YYYY")} - ${dateRange.endDate.format("DD MMM YYYY")}`} readOnly />
                  <i className="fa fa-chevron-down text-muted" />
                </div>
              </DatePicker>
              <Button className="ns-posbills-icon-action" onClick={() => handleDateChange("next")}>›</Button>
              <Popover content={filterContent} title="Filters" trigger="click" placement="bottomRight">
                <Button className="ns-posbills-action-btn">
                  <Filter size={16} /> Filters
                  {activeFilterCount ? <span className="ns-tab-count">{activeFilterCount}</span> : null}
                </Button>
              </Popover>
              <Button className="ns-posbills-icon-action" onClick={() => fetchOrders(dateRange.startDate, dateRange.endDate)} loading={listLoading} title="Refresh">
                <RefreshCw size={16} />
              </Button>
              <Link to="#" className="btn ns-posbills-icon-action" ref={maximizeBtnRef}>
                {isFullScreen ? <Minimize size={16} /> : <Maximize size={16} />}
              </Link>
              <Dropdown overlay={downloadMenu} trigger={["click"]} placement="bottomRight">
                <Button type="primary" className="ns-posbills-action-btn">
                  <Download size={16} /> Download <DownOutlined />
                </Button>
              </Dropdown>
            </div>
          </div>

          {/* Stats */}
          <div className="ns-posbills-stats">
            {renderStatCard({ label: "Total Bills",   value: pageStats.totalBills,       helper: `${dateRange.startDate.format("DD MMM")} – ${dateRange.endDate.format("DD MMM YYYY")}`, icon: <FileText size={19} />,   tone: "blue"   })}
            {renderStatCard({ label: "Settled Bills", value: pageStats.settledBills,     helper: "Closed and paid records",           icon: <CheckCircle size={19} />, tone: "green"  })}
            {renderStatCard({ label: "Outstanding",   value: pageStats.outstandingBills, helper: `${formatMoney(pageStats.outstandingRevenue)} pending`, icon: <AlertCircle size={19} />, tone: "orange" })}
            {renderStatCard({ label: "Total Revenue", value: formatMoney(pageStats.totalRevenue), helper: `${pageStats.complementaryBills} complementary bill(s)`, icon: <DollarSign size={19} />, tone: "purple" })}
          </div>
        </div>

        {/* ── PDF report preview (report-level, not bill preview) ── */}
        <PrintReportPreview
          visible={pdfVisible}
          onClose={() => { setPdfVisible(false); if (pdfData) { URL.revokeObjectURL(pdfData); setPdfData(null); } }}
          reportTitle="Sales Register"
          propertyName="Your Property Name"
          data={exportData(activeTab === "1" ? filteredSettledOrders : filteredUnsettledOrders)}
          headers={exportHeaders}
          footerData={getTotals(activeTab === "1" ? filteredSettledOrders : filteredUnsettledOrders)}
          totalColumns={["subtotal", ...applicableTaxes, "tax", "delivery_charge", "grand_total"]}
          onDownloadPDF={handleExportPDF}
          apiEndpoint="pos/orders/generate-pdf"
          apiUrl={apiURL}
        />

        {/* ── Bill preview drawer ── */}
        {showPreview && (
          previewLoading
            ? (
              <div style={{
                position: "fixed", inset: 0, zIndex: 2000,
                background: "rgba(15,23,42,0.38)",
                display: "flex", alignItems: "center", justifyContent: "center",
              }}>
                <div style={{ background: "#fff", borderRadius: 20, padding: "24px 32px", display: "flex", gap: 14, alignItems: "center", boxShadow: "0 18px 48px rgba(15,23,42,0.12)" }}>
                  <Spin size="large" />
                  <span style={{ fontWeight: 900, color: "#64748b" }}>Loading bill...</span>
                </div>
              </div>
            )
            : (
              <BillPreviewDrawer
                order={previewOrder}
                settings={previewSettings}
                onClose={handleClosePrev}
                onReprint={() => handleReprint(previewOrder?.order_id || previewOrder?.id)}
                reprinting={reprinting}
              />
            )
        )}

        {/* ── Table area ── */}
        {listLoading ? (
          <div className="ns-modern-loader">
            <div className="ns-modern-loader-card">
              <Spin size="large" />
              <span>Loading POS bills...</span>
            </div>
          </div>
        ) : (
          <div className="ns-posbills-shell">
            <div className="ns-posbills-searchbar">
              <div className="ns-search-wrap">
                <Search
                  placeholder="Search order no, display no, room, phone, payment..."
                  allowClear enterButton="Search" size="large"
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                />
              </div>
              <div className="ns-active-context">
                <span>Viewing:</span>
                <strong>{activeTab === "1" ? "Settled Bills" : "Outstanding Bills"}</strong>
                <span>·</span>
                <span>{activeTabData.length} result(s)</span>
              </div>
            </div>
            {error ? (
              <p className="text-danger text-center p-3">{error}</p>
            ) : (
              <Tabs className="ns-posbills-tabs" activeKey={activeTab} onChange={setActiveTab}>
                <TabPane
                  tab={<span className="fw-bold d-flex align-items-center" style={{ fontSize: "1.05rem" }}><CheckCircle size={20} className="me-2 text-success" /> Settled Bills ({settledOrders.length})</span>}
                  key="1"
                >
                  {filteredSettledOrders.length === 0
                    ? emptyState("No Settled Bills", "There are no settled bills in the selected date range.")
                    : renderTable(filteredSettledOrders, false)}
                </TabPane>
                <TabPane
                  tab={<div className="d-flex align-items-center text-danger fw-bold" style={{ fontSize: "1.1rem" }}><AlertCircle size={20} className="me-2" /> Outstanding Bills ({unsettledOrders.length})</div>}
                  key="2"
                >
                  {filteredUnsettledOrders.length === 0
                    ? emptyState("No Outstanding Bills", "There are no outstanding bills in the selected date range.")
                    : renderTable(filteredUnsettledOrders, true)}
                </TabPane>
              </Tabs>
            )}
          </div>
        )}

        {/* ── Settle order modal (preserved verbatim) ── */}
        {modalVisible && selectedOrder && (
          <div id="settle_order" className="modal custom-modal fade show" style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}>
            <div className="modal-dialog modal-lg shadow-lg" style={{ margin: 0, position: "fixed", right: 0, height: "100vh", width: "40%", borderRadius: "10px 0 0 10px", overflow: "hidden", background: "linear-gradient(135deg,#ffffff,#f8f9fa)" }} role="document">
              <Form form={form} style={{ height: "100%", display: "flex", flexDirection: "column" }}>
                <div className="modal-content h-100 border-0">
                  <div className="modal-header bg-primary text-white d-flex justify-content-between align-items-center" style={{ borderBottom: "2px solid #e9ecef", padding: "1rem 1.5rem" }}>
                    <h5 className="modal-title fw-bold m-0" style={{ fontSize: "1.3rem" }}>Settle Order #{selectedOrder.order_number}</h5>
                    <button type="button" className="btn-close btn-close-white" aria-label="Close" onClick={() => setModalVisible(false)} />
                  </div>
                  <div className="modal-body p-4" style={{ maxHeight: "calc(100vh - 180px)", overflowY: "auto", background: "#fff" }}>
                    <div className="mb-4 p-3 bg-light rounded shadow-sm">
                      <p className="mb-2"><strong className="text-muted">Order Type:</strong> <span className="fw-bold">{selectedOrder.order_type}</span></p>
                      <p className="mb-0"><strong className="text-muted">Grand Total:</strong> <span className="fw-bold text-danger">{currencyCode}{(selectedOrder.grand_total || 0).toFixed(2)}</span></p>
                    </div>
                    <Form.List name="payments">
                      {(fields, { add, remove }) => (
                        <>
                          {fields.map((field) => (
                            <div key={field.key} className="d-flex align-items-center mb-3 p-2 bg-light rounded shadow-sm">
                              <Form.Item {...field} name={[field.name, "method"]} fieldKey={[field.fieldKey, "method"]} rules={[{ required: true, message: "Payment method is required" }]} style={{ width: "35%", marginRight: "10px" }}>
                                <Select options={[{ value: "Cash", label: "Cash" }, { value: "Card", label: "Card" }, { value: "Room Credit", label: "Room Credit" }]} placeholder="Payment Method" styles={{ control: (b) => ({ ...b, borderRadius: "8px" }) }} />
                              </Form.Item>
                              <Form.Item {...field} name={[field.name, "amount"]} fieldKey={[field.fieldKey, "amount"]} rules={[{ required: true, message: "Amount is required" }]} style={{ width: "25%", marginRight: "10px" }}>
                                <InputNumber min={0} max={selectedOrder.grand_total} placeholder="Amount" style={{ width: "100%", borderRadius: "8px" }} addonBefore={currencyCode} />
                              </Form.Item>
                              {fields.length > 1 && <button className="btn btn-link text-danger" onClick={() => remove(field.name)}><i className="fas fa-trash" /></button>}
                            </div>
                          ))}
                          <button className="btn btn-outline-secondary btn-block" onClick={() => add()} style={{ borderRadius: "8px", marginBottom: "20px" }}>
                            <i className="fas fa-plus me-2" /> Add Payment Method
                          </button>
                        </>
                      )}
                    </Form.List>
                  </div>
                  <div className="modal-footer p-3 bg-light border-top">
                    <button className="btn btn-secondary me-2" onClick={() => setModalVisible(false)} style={{ borderRadius: "8px", padding: "8px 20px" }}>Cancel</button>
                    <button className="btn btn-primary" disabled={listLoading} onClick={handleModalOk} style={{ borderRadius: "8px", padding: "8px 20px" }}>
                      {listLoading ? "Settling..." : "Settle Order"}
                    </button>
                  </div>
                </div>
              </Form>
            </div>
          </div>
        )}

        <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} closeOnClick pauseOnHover draggable />
      </div>
    </div>
  );
};

export default PosBillsList;